import {AfterViewInit, effect, ElementRef, inject, Injectable, InputSignal, InputSignalWithTransform, OnDestroy, ViewChild} from '@angular/core';
import {Chart, ChartConfiguration, ChartData, ChartDataset, ChartType, Plugin}                                              from 'chart.js';
import {ChartConfsService}                                                                                                  from './chart-confs.service';
import {ChartThemeService}                                                                                                  from './chart-theme.service';


@Injectable()
export abstract class ChartCompBase<T extends ChartType, D> implements AfterViewInit, OnDestroy {
	@ViewChild('ChartItem') abstract chartItem: ElementRef<HTMLCanvasElement>;
	@ViewChild('LegendWrapper') abstract legendWrapper: ElementRef<HTMLDivElement>;

	public svcChartTheme = inject(ChartThemeService);
	public svcChartConfs = inject(ChartConfsService);

	abstract chartConf: InputSignal<ChartConfiguration<T, D[]>>;
	abstract disableLegend: InputSignalWithTransform<boolean, unknown>;

	chart?: Chart<T, D[]>;

	constructor() {
		effect(() => {
			//@ts-ignore
			this.chartConf().plugins = [
				...this.chartConf().plugins as Plugin<T, D>[],
				...this.disableLegend() ? [] : [this.htmlLegendPlugin]
			];
		});
	}

	ngAfterViewInit() {
		this.initializeChart();
	}

	ngOnDestroy() {
		this.destroyChart();
	}

	initializeChart() {
		if (this.chartConf()) {
			this.chart = new Chart<T, D[]>(this.chartItem.nativeElement, this.chartConf());
		}
	}

	updateData(newData: ChartData<T, D[]>) {
		if (this.chart) {
			this.chart.data = newData;
			this.chart.update();
		}
	}

	updateDataset(index: number, newDataset: Partial<ChartDataset>) {
		if (this.chart?.data.datasets[index]) {
			Object.assign(this.chart.data.datasets[index], newDataset);
			this.chart.update();
		}
	}

	/*updateScaleMax(scale: 'x' | 'y', newMax: number) {
	 if (this.chart?.config.options.scales[scale]) {
	 this.chart.config.options.scales[scale] = {
	 ...this.chart?.config.options.scales[scale],
	 max: newMax
	 };
	 this.chart.update();
	 // console.log('updating max', newMax, this.chart?.data.datasets)
	 }
	 }*/

	updateSingleDataPoint(datasetIndex: number, dataIndex: number, newValue: number) {
		const dataset = this.chart?.data.datasets[datasetIndex];
		if (dataset && dataset.data[dataIndex] !== undefined) {
			(dataset.data as number[])[dataIndex] = newValue;
			this.chart?.update();
		}
	}

	destroyChart() {
		if (this.chart) {
			this.chart.destroy();
		}
	}

	getOrCreateLegendList = (chart: Chart, id: string) => {
		let listContainer = this.legendWrapper.nativeElement.querySelector('div');

		if (!listContainer) {
			listContainer = document.createElement('div');
			listContainer.classList.add('chart-legend-wrapper');

			this.legendWrapper.nativeElement.appendChild(listContainer);
		}

		return listContainer;
	};

	htmlLegendPlugin = {
		id         : 'htmlLegend',
		afterUpdate: (chart: Chart, args: any, options: any) => {
			const ul = this.getOrCreateLegendList(chart, options.containerID);

			// Remove old legend items
			while (ul.firstChild) {
				ul.firstChild.remove();
			}

			// Reuse the built-in legendItems generator
			const items = chart.options.plugins?.legend?.labels?.generateLabels ? chart.options.plugins.legend.labels.generateLabels(chart) : [];

			items.forEach((item, i) => {
				const datasetItem = chart.data.datasets[i] as ChartDataset;
				const legendItem = document.createElement('div');

				legendItem.classList.add('chart-legend-item');

				//@ts-ignore
				const {type} = chart.config;

				legendItem.onclick = () => {
					if (type === 'pie' || type === 'doughnut') {
						// Pie and doughnut charts only have a single dataset and visibility is per item
						chart.toggleDataVisibility(item.index as number);
					} else {
						chart.setDatasetVisibility(
							item.datasetIndex as number,
							!chart.isDatasetVisible(item.datasetIndex as number)
						);
					}
					chart.update();
				};

				// Color box
				const boxSpan = document.createElement('span');

				boxSpan.classList.add('chart-legend-box');
				boxSpan.classList.add(`chart-legend-box-${datasetItem.type}`);

				if (datasetItem.type === 'line') {
					boxSpan.style.borderColor = datasetItem.borderColor as string || '#ff00ff';
					boxSpan.classList.add(`chart-legend-box-point-${(datasetItem as ChartDataset<'line'>).pointStyle}`);
				}

				if (datasetItem.type === 'bar' || datasetItem.type === undefined) {
					boxSpan.style.backgroundColor = datasetItem.backgroundColor as string || '#ff00ff';
				}


				if (item.text === 'NOW') {
					boxSpan.classList.add('animate-pulse');
				}


				const textContainer = document.createElement('p');
				textContainer.classList.add('chart-legend-txt');
				textContainer.style.textDecoration = item.hidden ? 'line-through' : '';

				/*if (datasetItem.themeConfig) {
				 boxSpan.style.background = (datasetItem.themeConfig as ChartThemeIntf)?.color?.base || '#ff00ff';
				 textContainer.style.color = (datasetItem.themeConfig as ChartThemeIntf)?.color?.base || '#ff00ff';
				 } else {
				 // boxSpan.style.background = item.fillStyle ? item.fillStyle.toString() : '#ff00ff';
				 // textContainer.style.color = item.fillStyle ? item.fillStyle.toString() : '#ff00ff';
				 }*/

				const text = document.createTextNode(item.text);
				textContainer.appendChild(text);

				legendItem.appendChild(boxSpan);
				legendItem.appendChild(textContainer);
				ul.appendChild(legendItem);
			});
		}
	};

	// private setChartOptions(): ChartOptions {
	// 	return {
	// 		...this.options(),
	// 		plugins: {
	// 			...this.options().plugins,
	// 			legend: {
	// 				display: false
	// 			}
	// 		}
	// 	};
	// }
}
